import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId || attempts >= 10) {
      if (attempts >= 10) setStatus('error');
      return;
    }

    try {
      const response = await api().get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  }, [sessionId, api, attempts]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]); // Only run once on mount

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md text-center" data-testid="payment-success-page">
        <CardHeader>
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin mx-auto text-primary mb-4" />
              <CardTitle>Processing Payment...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <CardTitle className="text-green-600">Payment Successful!</CardTitle>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
              <CardTitle className="text-red-600">Payment Issue</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          )}
          {status === 'success' && (
            <>
              <p className="text-muted-foreground">
                Thank you for subscribing to Forum Planner! Your account has been upgraded.
              </p>
              <Button onClick={() => navigate('/')} className="gap-2" data-testid="go-to-dashboard">
                Go to Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-muted-foreground">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => navigate('/settings')}>
                  Try Again
                </Button>
                <Button onClick={() => navigate('/')}>
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
